import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { formatPlayCount } from '../lib/utils';

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };

const fmtCurrency = (n) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const ACTIVITY_ICONS = {
  campaign_registered: '📝', link_submitted: '🔗', link_verified: '✅',
  metrics_synced: '📊', campaign_closed: '🔒', payout_paid: '💰', tier_upgraded: '⬆️',
};
const ACTIVITY_LABELS = {
  campaign_registered: 'Daftar Campaign', link_submitted: 'Submit Link',
  link_verified: 'Verified Link', metrics_synced: 'Sync Statistik',
  campaign_closed: 'Campaign Ditutup', payout_paid: 'Payout Dibayar', tier_upgraded: 'Naik Tier',
};

const HunterDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [hunterData, setHunterData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadHunterDetail(); }, [userId]); // eslint-disable-line

  const loadHunterDetail = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/api/hunters/${userId}`);
      setHunterData(data);
    } catch (error) {
      alert('Error: ' + error.message);
      navigate('/hunters');
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!hunterData) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center" style={card}>
        <p className="text-[14px] text-[#8a8a8a]">Hunter not found.</p>
        <button onClick={() => navigate('/hunters')} className="mt-2 text-[13px] text-[#3fb5d2] hover:text-[#2a798c] font-medium transition-colors">
          ← Kembali ke Hunters
        </button>
      </div>
    );
  }

  const { profile: hp, payouts, activity_history } = hunterData;
  const initials = hp.full_name?.charAt(0)?.toUpperCase() || '?';

  return (
    <div className="space-y-4">
      {/* Back + header */}
      <div>
        <button onClick={() => navigate('/hunters')}
          className="text-[13px] text-[#3fb5d2] hover:text-[#2a798c] font-medium transition-colors mb-2 inline-block">
          ← Kembali ke Hunters
        </button>
        <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Hunter Detail</h1>
      </div>

      {/* Hunter profile card */}
      <div className="bg-white rounded-2xl p-6" style={card}>
        <div className="flex items-center gap-4 mb-5">
          <div className="w-14 h-14 rounded-full bg-[#3fb5d2] flex items-center justify-center text-[20px] font-bold text-white flex-shrink-0">
            {initials}
          </div>
          <div>
            <p className="text-[17px] font-semibold text-[#3b3b3b]">{hp.full_name || 'N/A'}</p>
            <p className="text-[13px] text-[#8a8a8a]">{hp.email || '—'}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-[11px] text-[#8a8a8a] uppercase tracking-wider mb-1">Tier</p>
            {hp.current_tier ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-semibold bg-[#ecf8fb] text-[#2a798c] border border-[#3fb5d2]/30">
                {hp.current_tier.nama}
              </span>
            ) : <span className="text-[13px] text-[#8a8a8a]">No Tier</span>}
          </div>
          <div>
            <p className="text-[11px] text-[#8a8a8a] uppercase tracking-wider mb-1">Total Views</p>
            <p className="text-[15px] font-bold text-[#3b3b3b]">{formatPlayCount(hp.total_play_count || 0)}</p>
          </div>
          <div>
            <p className="text-[11px] text-[#8a8a8a] uppercase tracking-wider mb-1">Total Earned</p>
            <p className="text-[15px] font-bold text-[#2a798c]">{fmtCurrency(payouts?.settled?.total_amount || 0)}</p>
          </div>
          <div>
            <p className="text-[11px] text-[#8a8a8a] uppercase tracking-wider mb-1">Pending</p>
            <p className="text-[15px] font-bold text-amber-600">{fmtCurrency(payouts?.pending?.total_amount || 0)}</p>
          </div>
        </div>
      </div>

      {/* Payouts split */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Settled */}
        <div className="bg-white rounded-2xl p-5" style={card}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[14px] font-semibold text-[#3b3b3b]">Payout Settled</h3>
            <span className="text-[12px] text-[#8a8a8a]">{payouts?.settled?.count || 0} item</span>
          </div>
          {payouts?.settled?.items?.length === 0 ? (
            <p className="text-[13px] text-[#8a8a8a]">Tidak ada payout settled.</p>
          ) : (
            <div className="space-y-3 max-h-56 overflow-y-auto">
              {payouts.settled.items.map((p) => (
                <div key={p.id} className="flex items-start justify-between border-b border-[#e5e5e5] pb-3 last:border-0 last:pb-0">
                  <div>
                    <p className="text-[13px] font-medium text-[#3b3b3b]">{p.campaigns?.name || '—'}</p>
                    <p className="text-[11px] text-[#8a8a8a]">{fmtDate(p.paid_at)}</p>
                  </div>
                  <span className="text-[13px] font-semibold text-[#2a798c]">{fmtCurrency(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending */}
        <div className="bg-white rounded-2xl p-5" style={card}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[14px] font-semibold text-[#3b3b3b]">Payout Pending</h3>
            <span className="text-[12px] text-[#8a8a8a]">{payouts?.pending?.count || 0} item</span>
          </div>
          {payouts?.pending?.items?.length === 0 ? (
            <p className="text-[13px] text-[#8a8a8a]">Tidak ada payout pending.</p>
          ) : (
            <div className="space-y-3 max-h-56 overflow-y-auto">
              {payouts.pending.items.map((p) => (
                <div key={p.id} className="flex items-start justify-between border-b border-[#e5e5e5] pb-3 last:border-0 last:pb-0">
                  <div>
                    <p className="text-[13px] font-medium text-[#3b3b3b]">{p.campaigns?.name || '—'}</p>
                    <p className="text-[11px] text-[#8a8a8a]">{p.status === 'campaign_active' ? 'Campaign Aktif' : 'Menunggu Pembayaran'}</p>
                    {p.proposals?.social_media_link && (
                      <a href={p.proposals.social_media_link} target="_blank" rel="noopener noreferrer"
                        className="text-[11px] text-[#3fb5d2] hover:text-[#2a798c]">Lihat Link →</a>
                    )}
                  </div>
                  <span className="text-[13px] font-semibold text-amber-600">{fmtCurrency(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Activity history */}
      <div className="bg-white rounded-2xl p-5" style={card}>
        <h3 className="text-[15px] font-semibold text-[#3b3b3b] mb-4">History Aktivitas</h3>
        {activity_history?.length === 0 ? (
          <p className="text-[13px] text-[#8a8a8a]">Tidak ada aktivitas.</p>
        ) : (
          <div className="space-y-3">
            {activity_history?.map((a, i) => (
              <div key={i} className="flex items-start gap-3 p-3 border border-[#e5e5e5] rounded-xl hover:bg-gray-50/50 transition-colors">
                <span className="text-[18px] flex-shrink-0 mt-0.5">{ACTIVITY_ICONS[a.type] || '📌'}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-[13px] font-semibold text-[#3b3b3b]">{ACTIVITY_LABELS[a.type] || a.type}</span>
                    <span className="text-[11px] text-[#8a8a8a] flex-shrink-0">{fmtDate(a.timestamp)}</span>
                  </div>
                  <div className="mt-0.5 text-[12px] text-[#646464]">
                    {a.type === 'campaign_registered' && <p>Daftar ke campaign <strong>{a.data.campaign_name}</strong></p>}
                    {a.type === 'link_submitted' && <p>Submit link ke campaign <strong>{a.data.campaign_name}</strong></p>}
                    {a.type === 'link_verified' && <p>Link terverifikasi · <strong>{a.data.campaign_name}</strong></p>}
                    {a.type === 'metrics_synced' && (
                      <div>
                        <p>Sync statistik · <strong>{a.data.campaign_name}</strong></p>
                        <div className="flex gap-3 mt-1 text-[11px] text-[#8a8a8a]">
                          <span>Views: {formatPlayCount(a.data.play_count || 0)}</span>
                          <span>Likes: {formatPlayCount(a.data.like_count || 0)}</span>
                          <span>Comments: {formatPlayCount(a.data.comment_count || 0)}</span>
                        </div>
                      </div>
                    )}
                    {a.type === 'campaign_closed' && <p>Campaign <strong>{a.data.campaign_name}</strong> ditutup</p>}
                    {a.type === 'payout_paid' && <p>Payout <strong>{fmtCurrency(a.data.amount)}</strong> dari campaign <strong>{a.data.campaign_name}</strong></p>}
                    {a.type === 'tier_upgraded' && <p>Naik ke tier <strong>{a.data.tier_name}</strong></p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HunterDetail;
